import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import BoardWorkspace from "@/components/BoardWorkspace";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const filters = [
  { id: "open", label: "Open" },
  { id: "attention", label: "Urgent / Overdue by Project" },
  { id: "permit_renewals", label: "Permit Renewals" },
  { id: "permit_attention", label: "Permit Attention" },
  { id: "due_today", label: "Due Today" },
  { id: "overdue", label: "Overdue" },
  { id: "upcoming", label: "Upcoming" },
  { id: "completed", label: "Completed" },
];

const formatLabel = (value) => value.replaceAll("_", " ");

const actionTypeLabel = (item) => {
  const labels = {
    permit_renewal: "Permit renewal",
    permit_pending: "Permit stuck Pending",
    permit_missing_expiration: "Permit missing expiration",
    locate: "Locate / 811",
    locate_missing_expiration: "Locate / 811 missing date",
    water_locate_expiration: "Water Locate",
    roadblocked_project: "Roadblocked Project",
    appointment: "Appointment",
    communication_task: "Communication task",
  };
  return labels[item.item_type] || "Operational action";
};

export default function ActionQueuePage() {
  const [data, setData] = useState(null);
  const [activeFilter, setActiveFilter] = useState("open");
  const [completingIds, setCompletingIds] = useState({});
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id") || "";

  useEffect(() => {
    const controller = new AbortController();
    const params = {
      filter: activeFilter,
      search,
    };
    if (projectId) {
      params.project_id = projectId;
    }
    axios
      .get(`${API}/action-queue`, { params, signal: controller.signal })
      .then((response) => setData(response.data))
      .catch((error) => {
        if (error.code === "ERR_CANCELED") {
          return;
        }
        toast.error("Action Queue could not be loaded.");
        setData({
          items: [],
          summary: { urgent: 0, overdue: 0, due_today: 0 },
          filter_counts: {},
        });
      });
    return () => controller.abort();
  }, [activeFilter, projectId, refresh, search]);

  const completeItem = async (item) => {
    if (completingIds[item.id]) {
      return;
    }
    setCompletingIds((current) => ({ ...current, [item.id]: true }));
    try {
      await axios.post(`${API}/action-queue/${item.id}/complete`, { note: "" });
      setData((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          items: current.items.filter((row) => row.id !== item.id),
        };
      });
      toast.success("Action marked complete. Source records were not changed.");
      window.dispatchEvent(new Event("jfm:badge-refresh"));
      setRefresh((value) => value + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action could not be completed.");
    } finally {
      setCompletingIds((current) => {
        const { [item.id]: _completed, ...remaining } = current;
        return remaining;
      });
    }
  };

  if (!data) {
    return (
      <div className="loading-screen" data-testid="action-queue-loading">
        Loading action queue…
      </div>
    );
  }

  return (
    <BoardWorkspace
      eyebrow="Operational follow-through"
      headingTestId="action-queue-heading"
      subtitle={
        projectId
          ? "Operational follow-through for this Project."
          : "Project-linked operational work that needs a human decision."
      }
      title="Action Queue"
    >
      <section className="project-notes-panel" data-testid="action-queue-list">
        <div className="project-state-strip action-queue-summary">
          <article data-testid="action-queue-urgent">
            <strong>Urgent</strong>
            <span>{data.summary.urgent}</span>
          </article>
          <article data-testid="action-queue-overdue">
            <strong>Overdue</strong>
            <span>{data.summary.overdue}</span>
          </article>
          <article data-testid="action-queue-due-today">
            <strong>Due today</strong>
            <span>{data.summary.due_today}</span>
          </article>
        </div>
        <div className="action-queue-toolbar" data-testid="action-queue-toolbar">
          <div className="action-queue-filters" data-testid="action-queue-filters">
            {filters.map((filter) => (
              <button
                className={activeFilter === filter.id ? "is-active" : ""}
                data-testid={`action-queue-filter-${filter.id}`}
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                type="button"
              >
                {filter.label}
                <span data-testid={`action-queue-filter-count-${filter.id}`}>
                  {data.filter_counts?.[filter.id] || 0}
                </span>
              </button>
            ))}
          </div>
          <label className="action-queue-search" htmlFor="action-queue-search-input">
            <span>Search Action Queue</span>
            <input
              data-testid="action-queue-search-input"
              id="action-queue-search-input"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Project, action, assignee, or notes"
              value={search}
            />
          </label>
        </div>
        {data.items.length ? (
          <div className="action-queue-card-list" data-testid="action-queue-card-list">
            {data.items.map((item) => (
              <article
                aria-label={`${item.title} ${formatLabel(item.alert_state)}`}
                className={`project-note-entry action-queue-row ${
                  item.item_type === "permit_renewal" ? "permit-renewal-action" : ""
                }`}
                data-testid={`action-queue-item-${item.id}`}
                key={item.id}
              >
                <header className="action-queue-item-heading">
                  <small data-testid={`action-queue-item-type-${item.id}`}>
                    {actionTypeLabel(item)}
                  </small>
                  <small
                    className="action-queue-alert-badge"
                    data-testid={`action-queue-item-alert-${item.id}`}
                  >
                    {formatLabel(item.alert_state)}
                  </small>
                </header>
                <div className="action-queue-card-primary">
                  <ProjectContext item={item} />
                  <strong data-testid={`action-queue-item-title-${item.id}`}>
                    {item.title}
                  </strong>
                  {item.notes && (
                    <p data-testid={`action-queue-item-notes-${item.id}`}>{item.notes}</p>
                  )}
                  {item.status !== "open" && item.resolution?.note && (
                    <small data-testid={`action-queue-item-resolution-${item.id}`}>
                      {item.resolution.note}
                    </small>
                  )}
                </div>
                <dl className="action-queue-item-meta">
                  <div>
                    <dt>Responsible</dt>
                    <dd data-testid={`action-queue-item-responsible-${item.id}`}>
                      {item.assignee || "Owner/Admin review required"}
                    </dd>
                  </div>
                  <div>
                    <dt>{dueLabel(item)}</dt>
                    <dd data-testid={`action-queue-item-due-${item.id}`}>
                      {item.permit_expires_on || item.due_at || "No due date"}
                    </dd>
                  </div>
                </dl>
                <div className="action-queue-card-actions">
                  {item.item_type === "permit_renewal" && item.permit_id && (
                    <Link
                      className="action-queue-open-permit-button"
                      data-testid={`action-queue-open-permit-${item.id}`}
                      to={`/permits-locates?permit_id=${item.permit_id}`}
                    >
                      Open Permit
                    </Link>
                  )}
                  {item.item_type === "locate" && item.permit_id && (
                    <Link
                      className="action-queue-open-permit-button"
                      data-testid={`action-queue-open-locate-${item.id}`}
                      to={`/permits-locates?permit_id=${item.permit_id}`}
                    >
                      Open Locate
                    </Link>
                  )}
                  {item.status === "open" && (
                    <button
                      className="action-queue-complete-button"
                      data-testid={`action-queue-complete-${item.id}`}
                      disabled={Boolean(completingIds[item.id])}
                      onClick={() => completeItem(item)}
                      type="button"
                    >
                      {completingIds[item.id] ? "Marking complete…" : "Mark complete"}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="action-queue-empty-state" data-testid="action-queue-empty-state">
            No items match this view.
          </p>
        )}
      </section>
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function dueLabel(item) {
  if (item.item_type === "permit_renewal") return "Permit expires";
  if (item.item_type === "locate") return "Locate expires";
  if (item.item_type === "locate_missing_expiration") return "Fill in expiration";
  if (item.item_type === "water_locate_expiration") return "Water Locate expires";
  if (item.item_type === "water_locate_submission") return "Submission due";
  return "Due";
}

function ProjectContext({ item }) {
  const projectCode = item.project_code?.trim();
  const projectName = item.project_name?.trim();
  const label = projectCode
    ? projectCode === projectName
      ? projectCode
      : `${projectCode} — ${projectName || "Project"}`
    : projectName || "Project unavailable";

  if (item.project_available && item.project_id) {
    return (
      <Link
        className="action-queue-project-link"
        data-testid={`action-queue-item-project-${item.id}`}
        to={`/projects/${item.project_id}`}
      >
        {label}
      </Link>
    );
  }

  return (
    <span
      aria-label="Linked Project is unavailable or outside your company"
      className="action-queue-project-unavailable"
      data-testid={`action-queue-item-project-${item.id}`}
      title="Linked Project is unavailable or outside your company"
    >
      Project unavailable
    </span>
  );
}